import { CartLinesUpdateEvent } from '@shopify/events';
import { morphSection } from '@theme/section-renderer';

/**
 * <cart-upsell> — mini-carrusel de productos complementarios dentro del cart drawer (Horizon 4.1.1).
 *
 * - Carrito CON items (id="cart-upsell"): carga recomendaciones complementarias del primer
 *   producto del carrito vía Recommendations API + Section Rendering (section cart-upsell-recs)
 *   y permite agregar sin salir del drawer.
 * - Carrito VACÍO (class cart-upsell--empty): el markup ya viene server-rendered ("Los más
 *   vendidos"); aquí solo enganchamos el botón Agregar.
 *
 * Mecanismo de add (4.1.1): se dispara CartLinesUpdateEvent (de @shopify/events) con action:"add".
 * El propio elemento hace el fetch a cart_add_url pidiendo las sections de los cart-items-component
 * y resuelve la promesa con {cart, detail:{sections,...}}, igual que AddToCartComponent. El
 * cart-items-component escucha el evento y hace morph del drawer.
 *
 * data-skip-subtree-update en el markup -> el morph del theme preserva el contenido inyectado por JS.
 */
class CartUpsell extends HTMLElement {
  static observedAttributes = ['data-product-id'];

  #loadedFor = null;
  #controller = null;
  // LP-UPSELL-GA4
  #viewObserver = null;
  #viewSent = false;

  connectedCallback() {
    this.addEventListener('click', this.#onClick);
    // Solo el carrusel de complementarios (carrito con items) hace fetch.
    if (this.dataset.url) this.#load();
    // LP-UPSELL-GA4 — el markup del carrito vacío ya viene server-rendered.
    this.#observeView();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    this.#controller?.abort();
    this.#viewObserver?.disconnect();
  }

  attributeChangedCallback(name, oldVal, newVal) {
    if (name === 'data-product-id' && newVal && newVal !== this.#loadedFor && this.dataset.url) {
      this.#load();
    }
  }

  async #load() {
    const pid = this.dataset.productId;
    const base = this.dataset.url;
    if (!pid || !base) return;
    if (pid === this.#loadedFor && this.querySelector('.cart-upsell__card')) return;
    this.#loadedFor = pid;

    this.#controller?.abort();
    this.#controller = new AbortController();

    const sep = base.includes('?') ? '&' : '?';
    const url = `${base}${sep}product_id=${pid}&section_id=cart-upsell-recs&intent=complementary`;
    try {
      const res = await fetch(url, { signal: this.#controller.signal });
      if (!res.ok) return this.#empty();
      const html = await res.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const content = doc.querySelector('[data-cart-upsell-content]');
      const cards = content ? content.querySelectorAll('.cart-upsell__card') : [];
      if (!content || cards.length === 0) return this.#empty();
      this.replaceChildren(content);
      this.#observeView(true); // LP-UPSELL-GA4 — contenido nuevo, vista nueva
    } catch (e) {
      if (e.name !== 'AbortError') this.#empty();
    }
  }

  #empty() {
    this.replaceChildren();
    this.#loadedFor = null;
  }

  /* ─────────────────────────── LP-UPSELL-GA4 ───────────────────────────
     Embudo del upsell: vieron -> apretaron -> compraron. La compra ya se mide
     con la propiedad de línea `_origen: cart-upsell` (py/reporte_upsell.py);
     acá faltaban las dos primeras etapas.

     Eventos con nombre propio a propósito: NO duplican el `add_to_cart` que ya
     manda el web pixel nativo de Shopify ni tocan las conversiones de Google
     Ads / Meta. Todo dentro de try/catch: la analítica no puede romper el add.
  */
  #esVacio() {
    return this.classList.contains('cart-upsell--empty');
  }

  #bloque() {
    return this.#esVacio() ? 'mas_vendidos' : 'complementarios';
  }

  #track(nombre, params) {
    try {
      if (typeof window.gtag === 'function') {
        window.gtag('event', nombre, params);
      } else if (Array.isArray(window.dataLayer)) {
        window.dataLayer.push({ event: nombre, ...params });
      }
    } catch (_) {
      /* nunca romper el carrito por analítica */
    }
  }

  /**
   * Dispara `lp_upsell_view` la primera vez que el bloque entra en pantalla.
   * Con el drawer cerrado el elemento no tiene caja, así que el observer no
   * dispara: una "vista" = el drawer se abrió y el bloque quedó a la vista.
   * @param {boolean} [reset] - true cuando se reemplazó el contenido.
   */
  #observeView(reset = false) {
    if (reset) this.#viewSent = false;
    if (this.#viewSent) return;
    if (!this.querySelector('.cart-upsell__card')) return;

    this.#viewObserver?.disconnect();
    this.#viewObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting || this.#viewSent) continue;
          this.#viewSent = true;
          this.#viewObserver?.disconnect();

          const botones = [...this.querySelectorAll('[data-cart-upsell-add]')];
          this.#track('lp_upsell_view', {
            bloque: this.#bloque(),
            tarjetas: botones.length,
            skus: botones
              .map((b) => b.getAttribute('data-sku') || '')
              .filter(Boolean)
              .join(','),
          });
        }
      },
      { threshold: 0.2 }
    );
    this.#viewObserver.observe(this);
  }

  #onClick = async (event) => {
    const btn = event.target.closest('[data-cart-upsell-add]');
    if (!btn || btn.disabled) return;
    event.preventDefault();

    const variantId = btn.getAttribute('data-variant-id');
    if (!variantId) return;

    // LP-UPSELL-GA4 — el click se mide ANTES del fetch: nos interesa la
    // intención, no si el add terminó bien (los errores se ven en la consola).
    const tarjetas = [...this.querySelectorAll('[data-cart-upsell-add]')];
    this.#track('lp_upsell_click', {
      bloque: this.#bloque(),
      sku: btn.getAttribute('data-sku') || '',
      variant_id: variantId,
      titulo: btn.getAttribute('data-titulo') || '',
      posicion: tarjetas.indexOf(btn) + 1,
      value: Number(btn.getAttribute('data-precio') || 0),
      currency: 'CLP',
    });
    const addUrl = (window.Theme && window.Theme.routes && window.Theme.routes.cart_add_url) || this.dataset.cartAddUrl || '/cart/add.js';

    btn.disabled = true;
    btn.classList.add('is-loading');

    // section ids de todos los cart-items-component presentes (igual que AddToCartComponent).
    const sectionIds = [];
    document.querySelectorAll('cart-items-component').forEach((c) => {
      if (c instanceof HTMLElement && c.dataset.sectionId) sectionIds.push(c.dataset.sectionId);
    });

    const itemCount = 1;
    const deferred = CartLinesUpdateEvent.createPromise();

    // Anunciar el add para que el cart-items-component prepare su render.
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: variantId, quantity: itemCount }],
        promise: deferred.promise,
      })
    );

    const payload = {
      items: [{ id: Number(variantId), quantity: itemCount, properties: { _origen: 'cart-upsell' } }],
      sections: sectionIds.join(','),
      sections_url: window.location.pathname,
    };

    try {
      const res = await fetch(addUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(`add failed ${res.status}`);
      const response = await res.json();

      // Refrescar /cart.js para construir el objeto cart que espera el listener.
      const ajaxCart = await fetch(`${window.location.origin}/cart.js`).then((r) => r.json());

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
        detail: {
          items: ajaxCart.items,
          source: 'cart-upsell',
          sourceId: this.id || 'cart-upsell',
          itemCount,
          sections: response.sections,
          didError: false,
        },
      });

      btn.classList.remove('is-loading');
      btn.classList.add('is-added');

      // LP-CART-V2 — morfeamos el drawer nosotros mismos.
      // Verificado el 30-jul-2026: el #handleCartUpdate de component-cart-items.js NO
      // re-renderiza el drawer cuando el add sale de <cart-upsell> (ni desde el carrito
      // vacío, ni después de un morph nuestro): el producto entraba pero el drawer
      // seguía mostrando el estado anterior. morphSection con el HTML que ya viene en
      // la respuesta del add sí funciona, y es idempotente si el theme también morfea.
      {
        const sectionId = this.dataset.sectionId;
        const html = response.sections?.[sectionId];
        const veniaVacio = this.classList.contains('cart-upsell--empty');
        if (sectionId && html) {
          try {
            await morphSection(sectionId, html, { mode: 'hydration', injectStylesheet: veniaVacio });

            // Tras el morph el scroller queda abajo (el foco salta al footer) y el
            // carrito se abre mostrando solo los complementos: las filas de producto
            // quedan fuera de vista. Siempre volvemos al tope.
            const scroller = document.querySelector('#cart-drawer .cart-drawer__content');
            if (scroller) {
              scroller.scrollTop = 0;
              window.setTimeout(() => {
                scroller.scrollTop = 0;
              }, 450);
            }
          } catch (err) {
            console.warn('[cart-upsell] morph del drawer falló:', err);
          }
        }
      }

      // Quitar la tarjeta agregada del carrusel.
      const card = btn.closest('.cart-upsell__card');
      window.setTimeout(() => {
        card?.remove();
        if (!this.querySelector('.cart-upsell__card')) this.#empty();
      }, 650);
    } catch (e) {
      console.error('[cart-upsell]', e);
      try { deferred.reject(e); } catch (_) {}
      btn.classList.remove('is-loading');
      btn.disabled = false;
    }
  };
}

if (!customElements.get('cart-upsell')) {
  customElements.define('cart-upsell', CartUpsell);
}
